import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  UploadDocument,
  IAddress,
  IAuthorizationData,
  IAuthorizationDTO,
  ICDCode,
  IContact,
  ILocation,
  INewOrderingProvider,
  INewServicingProvider,
  IProductLine,
  IProviderResponseDTO,
  IReferences,
  IServiceLocation,
  IServicingProviderDTO,
} from '@core/model';
import { InboundFaxAction, PhysicianAccountPgpsDTO } from '@mynexus/mynexus-ui-lib';
import { IReferencesByEntity } from '@core/configuration';

type StepType = 'diagnosis' | 'orderingProvider' | 'servicingProvider';

@Injectable({
  providedIn: 'root',
})
export class AuthRequestInitialService {
  // for Authorization tab
  authorizationData$ = new BehaviorSubject<IAuthorizationData>({
    authorization: null,
    type: null,
    references: null,
    location: null,
    newAddress: null,
    contact: null,
  });

  authorizationByProductId$ = new BehaviorSubject<string>(null);

  serviceLocation$ = new BehaviorSubject<IServiceLocation>(null);

  externalRequestSource$ = new BehaviorSubject<IReferencesByEntity>(null);

  //for diagnosis tab
  selectedIcdCode$ = new BehaviorSubject<ICDCode[]>([]);

  // for DMEPOS (Servicing) Provider tab
  isAddNewServicingProvider$ = new BehaviorSubject<boolean>(false);
  selectedServicingProvider$ = new BehaviorSubject<IServicingProviderDTO>(null);
  listOfServicingProviders$ = new BehaviorSubject<IServicingProviderDTO[]>([]);
  newServicingProvider$ = new BehaviorSubject<INewServicingProvider>(null);
  servicingProviderPgpId$ = new BehaviorSubject<string>(null);

  // for Ordering Provider tab
  isAddNewPhysician$ = new BehaviorSubject<boolean>(false);
  selectedPhysician$ = new BehaviorSubject<PhysicianAccountPgpsDTO>(null);
  newPhysician$ = new BehaviorSubject<INewOrderingProvider>(null);

  // for Providers (DMEPOS and Ordering)
  authorizationProvider$ = new BehaviorSubject<IProviderResponseDTO>(null);

  // for Product Selection TEMP
  unsavedProductLines$ = new BehaviorSubject<Partial<IProductLine>[]>([]);
  unsavedChanges = new Map();

  //for Documents tab
  documents$ = new BehaviorSubject<UploadDocument[]>([]);
  noDocuments$ = new BehaviorSubject<boolean>(false);

  authorizationNumber$ = new BehaviorSubject<string>(null);

  // make sure that data for step is saved
  isSavedDataForStep$ = new BehaviorSubject<Record<StepType, boolean>>({
    diagnosis: false,
    orderingProvider: false,
    servicingProvider: false,
  });

  isResidentialAddressChanged$ = new BehaviorSubject<boolean>(false);

  ifaUploadRequestData$ = new BehaviorSubject<InboundFaxAction>(null);

  // for Authorization tab
  setAuthorization(authorization: IAuthorizationDTO): void {
    const authorizationData = this.authorizationData$.value;
    this.authorizationData$.next({
      ...authorizationData,
      authorization,
    });
  }

  setAuthorizationByProductId(id: string): void {
    this.authorizationByProductId$.next(id);
  }

  setServiceLocation(serviceLocation: IServiceLocation): void {
    this.serviceLocation$.next(serviceLocation);
  }

  setExternalRequestSource(requestSource: IReferencesByEntity): void {
    this.externalRequestSource$.next(requestSource);
  }

  setProductTypeForAuthorizationData(type: IReferencesByEntity): void {
    const authorizationData = this.authorizationData$.value;
    this.authorizationData$.next({
      ...authorizationData,
      type,
    });
  }

  setReferencesForAuthorizationData(references: IReferences): void {
    const authorizationData = this.authorizationData$.value;
    this.authorizationData$.next({
      ...authorizationData,
      references,
    });
  }

  setLocationForAuthorizationData(location: ILocation): void {
    const authorizationData = this.authorizationData$.value;
    this.authorizationData$.next({
      ...authorizationData,
      location,
    });
  }

  setNewAddressForAuthorizationData(newAddress: IAddress): void {
    const authorizationData = this.authorizationData$.value;
    this.authorizationData$.next({
      ...authorizationData,
      newAddress,
    });
  }

  setContactForAuthorizationData(contact: IContact): void {
    const authorizationData = this.authorizationData$.value;
    this.authorizationData$.next({
      ...authorizationData,
      contact,
    });
  }

  //for diagnosis tab
  setSelectedICDCodes(value: ICDCode[]) {
    this.selectedIcdCode$.next(value);
  }

  // for DMEPOS (Servicing) Provider tab
  setIsAddNewServicingProvider(val: boolean): void {
    this.isAddNewServicingProvider$.next(val);
  }

  setSelectedServicingProvider(val: IServicingProviderDTO): void {
    this.selectedServicingProvider$.next(val);
  }

  setListOfServicingProviders(val: IServicingProviderDTO[]): void {
    this.listOfServicingProviders$.next(val);
  }

  setNewServicingProvider(val: INewServicingProvider): void {
    this.newServicingProvider$.next(val);
  }

  setServicingProviderPgpId(val: string): void {
    this.servicingProviderPgpId$.next(val);
  }

  // for Ordering Provider tab
  setIsAddNewPhysician(val: boolean): void {
    this.isAddNewPhysician$.next(val);
  }

  setSelectedPhysician(val: PhysicianAccountPgpsDTO): void {
    this.selectedPhysician$.next(val);
  }

  setNewPhysician(val: INewOrderingProvider): void {
    this.newPhysician$.next(val);
  }

  // for Providers (DMEPOS and Ordering)
  setAuthorizationProvider(val: IProviderResponseDTO): void {
    this.authorizationProvider$.next(val);
  }

  //for Documents tab
  setDocuments(value: UploadDocument[]) {
    this.documents$.next(value);
  }

  setNoDocuments(val: boolean) {
    this.noDocuments$.next(val);
  }

  setIsResidentialAddressChanged(val: boolean) {
    this.isResidentialAddressChanged$.next(val);
  }

  // clean all data
  cleanAllAuthorizationData(): void {
    // clean data on Authorization tab
    const { type } = this.authorizationData$.value;
    this.authorizationData$.next({
      authorization: null,
      type,
      references: null,
      location: null,
      newAddress: null,
      contact: null,
    });
    this.authorizationByProductId$.next(null);
    this.serviceLocation$.next(null);

    // clean data on Diagnosis tab
    this.selectedIcdCode$.next([]);

    // clean data on Servicing Provider tab
    this.isAddNewServicingProvider$.next(false);
    this.selectedServicingProvider$.next(null);
    this.listOfServicingProviders$.next([]);
    this.newServicingProvider$.next(null);
    this.servicingProviderPgpId$.next(null);

    // clean data on Ordering Provider tab
    this.isAddNewPhysician$.next(false);
    this.selectedPhysician$.next(null);
    this.newPhysician$.next(null);

    // clean data on Providers (DMEPOS and Ordering)
    this.authorizationProvider$.next(null);

    this.unsavedProductLines$.next([]);
    this.unsavedChanges = new Map();

    // clean data on Documents tab
    this.documents$.next([]);
    this.noDocuments$.next(false);

    this.isResidentialAddressChanged$.next(false);

    this.authorizationNumber$.next(null);

    this.isSavedDataForStep$.next({
      diagnosis: false,
      orderingProvider: false,
      servicingProvider: false,
    });
  }

  setAuthorizationNumber(authorizationNumber: string): void {
    this.authorizationNumber$.next(authorizationNumber);
  }

  changeIsSavedDataForStep(step: StepType, value: boolean): void {
    const steps = this.isSavedDataForStep$.value;
    this.isSavedDataForStep$.next({ ...steps, [step]: value });
  }

  setIfaUploadRequestData(data: InboundFaxAction): void {
    this.ifaUploadRequestData$.next(data);
  }
}
